import axios from "axios";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";

const colors = {
  red: "#a20000",
  redDark: "#700000",
  redSoft: "#fbebeb",
  ink: "#171717",
  muted: "#666666",
  line: "#e8e8e8",
  surface: "#f7f7f7",
  white: "#ffffff",
};

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

const container = (max = 1200) => ({
  width: "100%",
  maxWidth: max,
  margin: "0 auto",
  padding: "0 20px",
});

const btnBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 9,
  padding: "13px 22px",
  borderRadius: 999,
  fontWeight: 800,
  border: "1px solid transparent",
  cursor: "pointer",
  textDecoration: "none",
  fontSize: 15,
};

const btnPrimary = { ...btnBase, background: colors.red, color: "#fff" };
const btnSecondary = { ...btnBase, borderColor: colors.red, color: colors.red, background: "#fff" };

const kicker = {
  color: colors.red,
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 1.2,
};

const eyebrow = {
  display: "inline-flex",
  padding: "7px 14px",
  borderRadius: 999,
  background: colors.redSoft,
  color: colors.red,
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: 0.8,
  textTransform: "uppercase",
};

const h2Style = {
  fontSize: "clamp(30px,3.8vw,46px)",
  lineHeight: 1.1,
  margin: "10px 0 14px",
  letterSpacing: -1.2,
  fontWeight: 900,
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${colors.line}` }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: "none",
          border: 0,
          padding: "20px 0",
          textAlign: "left",
          font: "inherit",
          fontWeight: 850,
          fontSize: 17,
          display: "flex",
          justifyContent: "space-between",
          cursor: "pointer",
          color: colors.ink,
        }}
      >
        {q}
        <span style={{ color: colors.red, fontSize: 18 }}>{open ? "−" : "+"}</span>
      </button>
      {open && <div style={{ padding: "0 0 20px", color: colors.muted }}>{a}</div>}
    </div>
  );
}

const onGridFaqs = [
  { q: "What is an on-grid inverter?", a: "An on-grid (grid-tied) inverter converts solar DC electricity into grid-compatible AC electricity for connected loads and export, where permitted." },
  { q: "What is anti-islanding protection?", a: "Standard on-grid inverters disconnect automatically during grid outages to protect utility workers. This safety feature is built into both GSPL-3000XT and GSPL-5000XT." },
  { q: "Can performance be monitored remotely?", a: "Yes. Both models include Wi-Fi and RS485 communication for compatible monitoring systems." },
  { q: "Are these inverters suitable for PM Surya Ghar?", a: "Yes, they are suitable for appropriately designed systems, subject to current scheme guidelines and DISCOM requirements." },
  { q: "What is the IP rating?", a: "Both models carry an IP65 enclosure rating for dust and water ingress protection." },
];

const keySpecs = [
  { value: "97.5%", label: "Maximum Efficiency" },
  { value: "70–500 V", label: "MPPT Range" },
  { value: "IP65", label: "Protection" },
  { value: "≤5 kg", label: "3 kW Weight" },
];

const overviewItems = [
  { n: "01", title: "High Efficiency", text: "Maximum efficiency of up to 97.5% and MPPT efficiency above 99%." },
  { n: "02", title: "Remote Monitoring", text: "Wi-Fi and RS485 communication for compatible monitoring systems." },
  { n: "03", title: "IP65 Enclosure", text: "Protection against dust and water ingress for suitable installation conditions." },
  { n: "04", title: "Quiet Operation", text: "Natural-convection cooling with operating noise below 25 dB." },
  { n: "05", title: "Compact Design", text: "Wall-mounted construction for easier handling and installation." },
  { n: "06", title: "Grid Ready", text: "Single-phase on-grid architecture for residential rooftop applications." },
];

const applicationItems = [
  { title: "Residential Rooftops", text: "For independent houses and residential buildings." },
  { title: "PM Surya Ghar Installations", text: "For appropriately designed systems, subject to current scheme and DISCOM requirements." },
  { title: "Shops and Offices", text: "For properties with regular daytime electricity consumption." },
  { title: "Clinics and Institutions", text: "For smaller facilities seeking rooftop solar savings." },
  { title: "Retail Outlets", text: "Efficient solar conversion for daily commercial loads." },
  { title: "Small Commercial Properties", text: "Suitable for appropriately sized single-phase systems." },
];

const models = [
  {
    model: "GSPL-3000XT",
    power: "3 kW",
    img: "/3KW-Inverter.webp",
    tagline: "Compact single-phase inverter for homes and small rooftop systems.",
    specs: [
      ["Rated AC Output", "3 kW"],
      ["Max DC Input", "3.9 kW"],
      ["MPPT Range", "70–500 V"],
      ["Maximum Efficiency", "97.5%"],
      ["Protection", "IP65"],
      ["Weight", "≤5 kg"],
    ],
  },
  {
    model: "GSPL-5000XT",
    power: "5 kW",
    img: "/5KW-Inverter.webp",
    tagline: "Higher-capacity inverter for larger homes, shops and small commercial properties.",
    specs: [
      ["Rated AC Output", "5 kW"],
      ["Max DC Input", "6.5 kW"],
      ["MPPT Range", "70–500 V"],
      ["Maximum Efficiency", "97.5%"],
      ["Protection", "IP65"],
      ["Weight", "9 kg"],
    ],
  },
];

const specRows = [
  ["Rated AC output power", "3 kW", "5 kW"],
  ["Maximum DC input power", "3.9 kW", "6.5 kW"],
  ["Maximum DC input voltage", "550 V", "550 V"],
  ["Start-up voltage", "80 V", "80 V"],
  ["MPPT operating range", "70–500 V", "70–500 V"],
  ["Maximum DC input current", "20 A", "20 A"],
  ["Maximum AC apparent power", "3.3 kVA", "5.5 kVA"],
  ["Maximum efficiency", "97.5%", "97.5%"],
  ["Euro efficiency", "97.3%", "97.3%"],
  ["Communication", "Wi-Fi / RS485", "Wi-Fi / RS485"],
  ["Protection rating", "IP65", "IP65"],
  ["Operating noise", "< 25 dB", "< 25 dB"],
  ["Dimensions", "290 × 290 × 120 mm", "347 × 386 × 167 mm"],
  ["Weight", "≤5 kg", "9 kg"],
];

const certifications = [
  { src: "/inverter01.webp", alt: "BIS certification", label: "BIS" },
  { src: "/inverter02.webp", alt: "IEC certification", label: "IEC" },
];

export default function OnGridInverterPage() {
  const width = useViewportWidth();
  const isTablet = width <= 980;
  const isMobile = width <= 640;

  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [formData, setFormData] = useState({ fullName: "", email: "", phone: "", model: "", remark: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => e.key === "Escape" && setLightboxSrc(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxSrc]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) { alert("Enter a valid email"); return false; }
    if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s+/g, ""))) { alert("Enter a valid 10-digit mobile"); return false; }
    if (formData.model === "") { alert("Select a model"); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      const utm = localStorage.getItem("utm_data") || null;
      const res = await axios.post("https://gautamsolar.us/submit-inverter", { ...formData, utm});
      if (res?.data?.success) {
        alert("Form submitted successfully!");
        localStorage.removeItem("utm_data");
        setFormData({ fullName: "", email: "", phone: "", model: "", remark: "" });
      }
    } catch (er) {
      console.log(er);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", color: colors.ink, background: "#fff", lineHeight: 1.6 }}>
      <Helmet>
        <title>On-Grid Solar Inverters | 3kW & 5kW Grid-Tied Inverters - Gautam Solar</title>
        <meta name="description" content="Gautam Solar GSPL-3000XT & GSPL-5000XT single-phase MPPT on-grid inverters. Up to 97.5% efficiency, IP65 protection, Wi-Fi/RS485 monitoring." />
        <link rel="canonical" href="https://gautamsolar.com/inverter/ongrid/" />
      </Helmet>

      {/* ================= HERO — Compact split ================= */}
      <header style={{ background: "#fff", borderBottom: `1px solid ${colors.line}`, position: "relative" }}>
        <div style={{ ...container(), display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1.15fr .85fr", gap: isTablet ? 40 : 56, alignItems: "center", padding: isMobile ? "52px 20px 44px" : "72px 20px 68px" }}>
          <div>
            <span style={eyebrow}>On-Grid Inverter Series · GSPL-XT</span>
            <h1 style={{ fontSize: "clamp(38px,5vw,66px)", lineHeight: 1.02, margin: "20px 0 18px", letterSpacing: -2.2, fontWeight: 900 }}>
              Solar energy,<br />
              <span style={{ color: colors.red }}>grid ready.</span>
            </h1>
            <p style={{ fontSize: 18, color: colors.muted, maxWidth: 520, margin: "0 0 28px" }}>
              Single-phase MPPT on-grid inverters available in 3 kW and 5 kW capacities for residential rooftop and small commercial solar systems.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="#models" style={btnPrimary}>Explore Models</a>
              <a href="#specs" style={btnSecondary}>Technical Specs</a>
            </div>
          </div>

          {/* Product image card with vertical side label */}
          <div style={{ position: "relative", display: "flex", alignItems: "stretch", gap: 14 }}>
            <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", color: colors.muted, fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", padding: "8px 0", alignSelf: "center" }}>
              GSPL-3000XT · GSPL-5000XT
            </div>
            <div style={{ flex: 1, background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: 20, padding: 18 }}>
              {/* <img src="/inverter1.jpeg" alt="Gautam Solar on-grid inverter" style={{ width: "100%", maxHeight: 460, objectFit: "contain", display: "block", borderRadius: 12 }} /> */}
              <img src="/3-5kw-inverter.webp" alt="Gautam Solar on-grid inverter" style={{ width: "100%", maxHeight: 460, objectFit: "contain", display: "block", borderRadius: 12 }} />
            </div>
          </div>
        </div>

        {/* Key spec band */}
        <div style={{ borderTop: `1px solid ${colors.line}`, background: "#fff" }}>
          <div style={{ ...container(), display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 0 }}>
            {keySpecs.map((s, i) => (
              <div
                key={s.label}
                style={{
                  padding: isMobile ? "22px 16px" : "28px 26px",
                  borderRight: isMobile ? (i % 2 === 0 ? `1px solid ${colors.line}` : "none") : (i < 3 ? `1px solid ${colors.line}` : "none"),
                  borderBottom: isMobile ? (i < 2 ? `1px solid ${colors.line}` : "none") : "none",
                }}
              >
                <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 900, color: colors.red, letterSpacing: -1, lineHeight: 1 }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 12, color: colors.muted, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", marginTop: 10 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ================= OVERVIEW — Editorial numbered list ================= */}
      <section style={{ padding: isMobile ? "56px 0" : "88px 0" }}>
        <div style={container()}>
          <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1.6fr", gap: isTablet ? 32 : 72, alignItems: "start" }}>
            {/* Left title column */}
            <div style={{ position: isTablet ? "static" : "sticky", top: 100 }}>
              <span style={kicker}>Product Overview</span>
              <h2 style={h2Style}>Built around efficiency,<br />made for rooftops.</h2>
              <p style={{ color: colors.muted, maxWidth: 380 }}>
                Available in 3 kW and 5 kW models, the GSPL series uses MPPT technology to optimise solar generation while supporting remote monitoring and dependable grid-connected operation.
              </p>
            </div>

            {/* Right numbered list */}
            <div>
              {overviewItems.map((item, i) => (
                <div
                  key={item.n}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "44px 1fr" : "60px 1fr",
                    gap: 20,
                    padding: "26px 0",
                    borderTop: i === 0 ? `1px solid ${colors.line}` : "none",
                    borderBottom: `1px solid ${colors.line}`,
                    alignItems: "start",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 900, color: colors.red, letterSpacing: 1, paddingTop: 4 }}>
                    /{item.n}
                  </span>
                  <div>
                    <h3 style={{ fontSize: isMobile ? 18 : 21, margin: "0 0 8px", fontWeight: 900, letterSpacing: -0.4 }}>
                      {item.title}
                    </h3>
                    <p style={{ color: colors.muted, margin: 0, fontSize: 15 }}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= MODELS — Horizontal wide cards ================= */}
      <section id="models" style={{ padding: isMobile ? "56px 0" : "88px 0", background: colors.surface, borderTop: `1px solid ${colors.line}`, borderBottom: `1px solid ${colors.line}` }}>
        <div style={container()}>
          <div style={{ marginBottom: 40 }}>
            <span style={kicker}>Two Models</span>
            <h2 style={h2Style}>Pick the capacity that fits your rooftop.</h2>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {models.map((m, idx) => (
              <article
                key={m.model}
                style={{
                  display: "grid",
                  gridTemplateColumns: isTablet ? "1fr" : "1fr 1.6fr",
                  background: "#fff",
                  border: `1px solid ${colors.line}`,
                  borderRadius: 24,
                  overflow: "hidden",
                }}
              >
                {/* Left image side */}
                <div
                  style={{
                    background: colors.surface,
                    display: "grid",
                    placeItems: "center",
                    padding: isMobile ? 24 : 32,
                    borderRight: isTablet ? "none" : `1px solid ${colors.line}`,
                    borderBottom: isTablet ? `1px solid ${colors.line}` : "none",
                    position: "relative",
                  }}
                >
                  <span style={{ position: "absolute", top: 20, left: 24, fontSize: 12, fontWeight: 900, letterSpacing: 1.5, color: colors.red, textTransform: "uppercase" }}>
                    0{idx + 1} / Model
                  </span>
                  <img src={m.img} alt={m.model} style={{ width: "100%", maxWidth: 300, maxHeight: 260, objectFit: "contain", display: "block", marginTop: 24 }} />
                </div>

                {/* Right content side */}
                <div style={{ padding: isMobile ? 24 : 34 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
                    <h3 style={{ fontSize: isMobile ? 24 : 30, margin: 0, letterSpacing: -1, fontWeight: 900 }}>
                      {m.model}
                    </h3>
                    <span style={{ padding: "5px 12px", borderRadius: 999, background: colors.redSoft, color: colors.red, fontWeight: 900, fontSize: 13 }}>
                      {m.power}
                    </span>
                  </div>
                  <p style={{ color: colors.muted, margin: "0 0 22px", maxWidth: 520 }}>{m.tagline}</p>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)",
                      gap: 0,
                      borderTop: `1px solid ${colors.line}`,
                      borderLeft: `1px solid ${colors.line}`,
                    }}
                  >
                    {m.specs.map(([k, v]) => (
                      <div
                        key={k}
                        style={{
                          padding: "14px 16px",
                          borderRight: `1px solid ${colors.line}`,
                          borderBottom: `1px solid ${colors.line}`,
                        }}
                      >
                        <div style={{ fontSize: 10, color: colors.muted, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase" }}>
                          {k}
                        </div>
                        <div style={{ fontWeight: 900, fontSize: 16, marginTop: 4, color: colors.ink }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <a href="#enquiry" style={{ ...btnPrimary, marginTop: 22 }}>
                    Get a Quote
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SPECS — Clean bordered table ================= */}
      <section id="specs" style={{ padding: isMobile ? "56px 0" : "88px 0" }}>
        <div style={container()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20, marginBottom: 32 }}>
            <div>
              <span style={kicker}>Technical Datasheet</span>
              <h2 style={h2Style}>Full specifications.</h2>
            </div>
            <p style={{ color: colors.muted, maxWidth: 320, margin: 0, fontSize: 14 }}>
              All values at STC unless noted.
            </p>
          </div>

          <div style={{ overflow: "auto", border: `1px solid ${colors.line}`, borderRadius: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720, background: "#fff" }}>
              <thead>
                <tr>
                  <th style={{ padding: "16px 20px", textAlign: "left", background: colors.surface, color: colors.ink, fontSize: 12, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase", borderBottom: `1px solid ${colors.line}` }}>
                    Parameter
                  </th>
                  <th style={{ padding: "16px 20px", textAlign: "left", background: colors.red, color: "#fff", fontSize: 12, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase" }}>
                    GSPL-3000XT
                  </th>
                  <th style={{ padding: "16px 20px", textAlign: "left", background: colors.red, color: "#fff", fontSize: 12, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase" }}>
                    GSPL-5000XT
                  </th>
                </tr>
              </thead>
              <tbody>
                {specRows.map((row, i) => (
                  <tr key={row[0]} style={{ background: i % 2 === 1 ? colors.surface : "#fff" }}>
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        style={{
                          padding: "14px 20px",
                          borderBottom: i === specRows.length - 1 ? "none" : `1px solid ${colors.line}`,
                          textAlign: "left",
                          fontWeight: j === 0 ? 500 : 800,
                          color: j === 0 ? colors.muted : colors.ink,
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ================= APPLICATIONS — Bordered grid ================= */}
      <section style={{ padding: isMobile ? "56px 0" : "88px 0", background: colors.redSoft }}>
        <div style={container()}>
          <div style={{ marginBottom: 40 }}>
            <span style={{ ...kicker, color: colors.redDark }}>Applications</span>
            <h2 style={{ ...h2Style, color: colors.ink }}>Where these inverters fit.</h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)",
              gap: 0,
              borderTop: `1px solid ${colors.red}`,
              borderLeft: `1px solid ${colors.red}`,
            }}
          >
            {applicationItems.map((a, i) => (
              <div
                key={a.title}
                style={{
                  padding: "28px 24px",
                  borderRight: `1px solid ${colors.red}`,
                  borderBottom: `1px solid ${colors.red}`,
                  background: "rgba(255,255,255,.4)",
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 900, color: colors.red, letterSpacing: 1 }}>
                  /{String(i + 1).padStart(2, "0")}
                </span>
                <h3 style={{ fontSize: 19, margin: "14px 0 8px", fontWeight: 900, letterSpacing: -0.3 }}>{a.title}</h3>
                <p style={{ color: colors.muted, margin: 0, fontSize: 15 }}>{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CERTIFICATIONS ================= */}
      <section style={{ padding: isMobile ? "56px 0" : "88px 0" }}>
        <div style={container()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20, marginBottom: 32 }}>
            <div>
              <span style={kicker}>Certifications</span>
              <h2 style={h2Style}>Certified for quality and safety.</h2>
            </div>
            <span style={{ ...kicker, color: colors.muted }}>Click to enlarge</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 22 }}>
            {certifications.map((c) => (
              <button
                key={c.src}
                type="button"
                onClick={() => setLightboxSrc(c)}
                style={{
                  background: "#fff",
                  border: `1px solid ${colors.line}`,
                  borderRadius: 20,
                  padding: 18,
                  cursor: "zoom-in",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  font: "inherit",
                  textAlign: "left",
                }}
              >
                <div style={{ background: colors.surface, borderRadius: 14, minHeight: 340, display: "grid", placeItems: "center", padding: 12 }}>
                  <img src={c.src} alt={c.alt} style={{ maxWidth: "100%", maxHeight: 460, objectFit: "contain" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: colors.red, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  {c.label} — Click to enlarge
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FAQ — Two-column ================= */}
      <section style={{ padding: isMobile ? "56px 0" : "88px 0", background: colors.surface, borderTop: `1px solid ${colors.line}` }}>
        <div style={{ ...container(), display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1.5fr", gap: isTablet ? 32 : 72 }}>
          <div style={{ position: isTablet ? "static" : "sticky", top: 100, alignSelf: "start" }}>
            <span style={kicker}>FAQs</span>
            <h2 style={h2Style}>Answers to<br />common questions.</h2>
            <p style={{ color: colors.muted, maxWidth: 320 }}>
              Still need help? Reach out to our team for a personalised recommendation.
            </p>
          </div>
          <div style={{ borderTop: `1px solid ${colors.line}` }}>
            {onGridFaqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ================= ENQUIRY ================= */}
      <section id="enquiry" style={{ padding: isMobile ? "56px 0" : "80px 0" }}>
        <div style={container()}>
          <div
            style={{
              background: `linear-gradient(135deg, ${colors.red}, ${colors.redDark})`,
              color: "#fff",
              borderRadius: 26,
              padding: isMobile ? 28 : 44,
              display: "grid",
              gridTemplateColumns: isTablet ? "1fr" : "1fr 1.2fr",
              gap: 36,
              alignItems: "center",
            }}
          >
            <div>
              <span style={{ ...eyebrow, background: "rgba(255,255,255,.15)", color: "#fff" }}>Product Enquiry</span>
              <h2 style={{ margin: "12px 0 12px", color: "#fff", fontSize: "clamp(26px,3vw,36px)", letterSpacing: -1, lineHeight: 1.15 }}>
                Find the right on-grid inverter for your rooftop.
              </h2>
              <p style={{ color: "rgba(255,255,255,.8)", margin: 0 }}>
                Share your requirement and our team will get back within 24 hours with the right model recommendation.
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full name"
                style={{ background: "rgb(255, 255, 255)", border: "1px solid rgba(255,255,255,.3)", color: "#fff", padding: "13px 15px", borderRadius: 12, font: "inherit" }}
              />
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Mobile number"
                style={{ background: "rgb(255, 255, 255)", border: "1px solid rgba(255,255,255,.3)", color: "#fff", padding: "13px 15px", borderRadius: 12, font: "inherit" }}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email address"
                style={{ gridColumn: "1/-1", background: "rgb(255, 255, 255)", border: "1px solid rgba(255,255,255,.3)", color: "#fff", padding: "13px 15px", borderRadius: 12, font: "inherit" }}
              />
              <select
                name="model"
                value={formData.model}
                onChange={handleChange}
                style={{ gridColumn: "1/-1", background: "rgba(255, 255, 255, 0.13)", border: "1px solid rgba(255,255,255,.3)", color: "#fff", padding: "13px 15px", borderRadius: 12, font: "inherit" }}
              >
                <option value="" style={{ color: "#000" }}>Select model</option>
                <option value="3kW" style={{ color: "#000" }}>3 kW — GSPL-3000XT</option>
                <option value="5kW" style={{ color: "#000" }}>5 kW — GSPL-5000XT</option>
                <option value="not-sure" style={{ color: "#000" }}>Not sure — recommend</option>
              </select>
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                placeholder="Project details"
                style={{ gridColumn: "1/-1", background: "rgb(255, 255, 255)", border: "1px solid rgba(255,255,255,.3)", color: "#fff", padding: "13px 15px", borderRadius: 12, font: "inherit", minHeight: 100, resize: "vertical" }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                style={{ ...btnBase, gridColumn: "1/-1", background: "#fff", color: colors.red, opacity: isSubmitting ? 0.7 : 1 }}
              >
                {isSubmitting && (
                  <span style={{ width: 14, height: 14, border: `2px solid ${colors.redSoft}`, borderTopColor: colors.red, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
                )}
                {isSubmitting ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 9999, cursor: "zoom-out" }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            aria-label="Close"
            style={{ position: "absolute", top: 20, right: 20, width: 44, height: 44, borderRadius: "50%", border: "1px solid #444", background: "#1a1a1a", color: "#fff", fontSize: 22, cursor: "pointer" }}
          >
            ×
          </button>
          <img
            src={lightboxSrc.src}
            alt={lightboxSrc.alt}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain", background: "#fff", borderRadius: 8 }}
          />
        </div>
      )}
    </div>
  );
}